import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { AxiosInstance } from "axios";
import {
  getActiveRealm,
  setActiveRealm,
  clearActiveRealm,
  clearRealmAuthTokens,
  storeRealmTokenResponse,
  type ActiveRealmData,
} from "@/services/realmTokenStorage";
import { createRealmApiClient } from "@/services/realmApiClient";
import type { TokenResponse } from "@/types";

interface RealmContextValue {
  activeRealm: ActiveRealmData | null;
  realmApi: AxiosInstance | null;
  realmLogin: (
    realm: ActiveRealmData,
    tokenResponse: TokenResponse,
  ) => void;
  realmLogout: () => void;
}

const RealmContext = createContext<RealmContextValue | undefined>(undefined);

export function RealmProvider({ children }: { children: ReactNode }) {
  const [activeRealm, setActiveRealmState] = useState<ActiveRealmData | null>(
    () => getActiveRealm(),
  );

  const realmLogin = useCallback(
    (realm: ActiveRealmData, tokenResponse: TokenResponse) => {
      storeRealmTokenResponse(realm.uuid, tokenResponse);
      setActiveRealm(realm);
      setActiveRealmState(realm);
    },
    [],
  );

  const realmLogout = useCallback(() => {
    if (activeRealm) {
      clearRealmAuthTokens(activeRealm.uuid);
    }
    clearActiveRealm();
    setActiveRealmState(null);
  }, [activeRealm]);

  const realmApi = useMemo<AxiosInstance | null>(() => {
    if (!activeRealm) return null;
    return createRealmApiClient(
      activeRealm.domain,
      activeRealm.uuid,
      realmLogout,
    );
  }, [activeRealm, realmLogout]);

  const value = useMemo<RealmContextValue>(
    () => ({
      activeRealm,
      realmApi,
      realmLogin,
      realmLogout,
    }),
    [activeRealm, realmApi, realmLogin, realmLogout],
  );

  return (
    <RealmContext.Provider value={value}>{children}</RealmContext.Provider>
  );
}

export function useRealmContext(): RealmContextValue {
  const ctx = useContext(RealmContext);
  if (!ctx) {
    throw new Error("useRealmContext must be used within a RealmProvider");
  }
  return ctx;
}